/**
 * Tailwind `md` in src/styles/common.css. At this width the desktop shell shows
 * (sidebar, no mobile bottom-bar logout).
 */
export const DESKTOP_SHELL_MIN_WIDTH = 900;

/**
 * Tailwind `sm`. Phone screens sit under this on the short side. Tablets and
 * desktop monitors do not, so a wide window there is a real desktop layout.
 */
export const PHONE_SHORT_SIDE_MAX = 600;

/** How far innerWidth may drift from a screen edge and still count as device-width. */
export const SCREEN_EDGE_TOLERANCE = 48;

export const ANDROID_MOBILE_LAYOUT_RELOAD_KEY = "artis_android_mobile_layout_reload";

/** Set by index.html for the document that is about to reload. Does not survive the reload. */
export const ANDROID_MOBILE_LAYOUT_RELOAD_FLAG = "__ARTIS_MOBILE_LAYOUT_RELOAD";

declare global {
  interface Window {
    [ANDROID_MOBILE_LAYOUT_RELOAD_FLAG]?: boolean;
  }
}

/**
 * The reload script runs before the module, but a deferred module can still start
 * in the same turn as location.reload(). Booting Auth0 then would consume the
 * one-time code on the desktop-width document.
 */
if (typeof window !== "undefined" && window[ANDROID_MOBILE_LAYOUT_RELOAD_FLAG]) {
  throw new Error("Reloading to apply the phone layout");
}

export type AndroidLayoutSnapshot = {
  userAgent: string;
  search: string;
  screenWidth: number;
  screenHeight: number;
  innerWidth: number;
  alreadyReloaded: boolean;
};

/**
 * True when the Auth0 return document is showing the desktop shell on a phone.
 *
 * Chrome on Android keeps the previous page's layout viewport on the cross-origin
 * return from Auth0 (often the 980px fallback). replaceState then stays in that
 * document, so the desktop shell stays up until a manual refresh. A same-origin
 * reload applies width=device-width. iPhone already applies it on the return
 * document, and a phone in landscape matches a screen edge, so neither reloads.
 */
export function shouldReloadAndroidAuthCallbackForMobileLayout(snapshot: AndroidLayoutSnapshot): boolean {
  if (snapshot.alreadyReloaded) return false;

  const params = new URLSearchParams(snapshot.search.startsWith("?") ? snapshot.search.slice(1) : snapshot.search);
  if (!params.get("code") || !params.get("state")) return false;

  if (/iPad|iPhone|iPod/.test(snapshot.userAgent)) return false;

  const shortSide = Math.min(snapshot.screenWidth, snapshot.screenHeight);
  if (!(shortSide > 0 && shortSide < PHONE_SHORT_SIDE_MAX)) return false;

  if (snapshot.innerWidth < DESKTOP_SHELL_MIN_WIDTH) return false;

  const nearScreenEdge =
    Math.abs(snapshot.innerWidth - snapshot.screenWidth) <= SCREEN_EDGE_TOLERANCE ||
    Math.abs(snapshot.innerWidth - snapshot.screenHeight) <= SCREEN_EDGE_TOLERANCE;
  return !nearScreenEdge;
}
